<template>
  <div v-if="visible" class="bbl-upload-modal-overlay" @click="handleOverlayClick">
    <div class="bbl-upload-modal" @click.stop>
      <div class="bbl-modal-header">
        <h3>Insert Image</h3>
        <button class="close-btn" @click="close">&times;</button>
      </div>
      
      <div class="bbl-modal-body">
        <div class="tab-buttons">
          <button
            :class="['tab-btn', { active: activeTab === 'upload' }]"
            @click="activeTab = 'upload'"
          >
            Upload File
          </button>
          <button
            :class="['tab-btn', { active: activeTab === 'url' }]"
            @click="activeTab = 'url'"
          >
            From URL
          </button>
        </div>
        
        <div v-if="activeTab === 'upload'" class="tab-content">
          <div
            v-if="!previewUrl"
            class="drop-zone"
            :class="{ 'drag-over': isDragOver }"
            @click="$refs.fileInput.click()"
            @dragover.prevent="isDragOver = true"
            @dragleave="isDragOver = false"
            @drop.prevent="handleDrop"
          >
            <div class="drop-zone-icon">📁</div>
            <div class="drop-zone-title">Drop image here or click to browse</div>
            <div class="drop-zone-subtitle">Supports: JPG, PNG, GIF, WebP (max {{ Math.round(maxFileSize / (1024 * 1024)) }}MB)</div>
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleFileSelect"
            >
          </div>
          
          <div v-else class="preview-container">
            <img :src="previewUrl" alt="Preview" class="preview-image">
            <div class="preview-info">
              <div class="preview-filename">{{ selectedFile && selectedFile.name }}</div>
              <div class="preview-size">{{ formatFileSize(selectedFile && selectedFile.size) }}</div>
            </div>
            
            <!-- Upload Success -->
            <div v-if="!isUploading && !uploadError && uploadedUrl" class="upload-success">
              <div class="success-message">
                <span class="success-icon">✅</span>
                Upload complete! Ready to insert.
              </div>
            </div>
            
            <!-- Upload Progress -->
            <div v-if="isUploading" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
              </div>
              <div class="progress-text">Uploading... {{ uploadProgress }}%</div>
            </div>
            
            <!-- Upload Error -->
            <div v-if="uploadError" class="upload-error">
              <div class="error-message">
                <span class="error-icon">⚠️</span>
                {{ uploadError }}
              </div>
              <div class="error-actions">
                <button class="retry-btn" @click="retryUpload(selectedFile)">Try Again</button>
                <button class="local-btn" @click="insertLocalImage">Use Local File</button>
              </div>
            </div>
            
            <button v-if="!isUploading && !uploadError" class="change-btn" @click="changeFile">Change Image</button>
          </div>
        </div>
        
        <div v-if="activeTab === 'url'" class="tab-content">
          <input
            v-model="imageUrl"
            type="url"
            class="url-input"
            placeholder="https://example.com/image.jpg"
          >
          <div class="url-help">Enter a direct link to an image file</div>
        </div>
      </div>
      
      <div class="bbl-modal-footer">
        <button class="cancel-btn" @click="close">Cancel</button>
        <button 
          class="insert-btn" 
          @click="insertImage"
          :disabled="isUploading || (activeTab === 'upload' && !selectedFile) || (activeTab === 'url' && !imageUrl.trim())"
        >
          <span v-if="isUploading">Uploading...</span>
          <span v-else>Insert Image</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadMixin } from '@/mixins/uploadMixin.js'

export default {
  name: 'ImageModal',
  mixins: [uploadMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'insert'],
  data() {
    return {
      activeTab: 'upload',
      imageUrl: '',
      isDragOver: false,
      selectedFile: null,
      previewUrl: null
    }
  },
  methods: {
    close() {
      this.activeTab = 'upload'
      this.imageUrl = ''
      this.selectedFile = null
      this.isDragOver = false
      this.resetUploadState()
      this.clearPreview()
      this.$emit('close')
    },
    
    clearPreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
      }
    },
    
    changeFile() {
      this.clearPreview()
      this.selectedFile = null
      this.resetUploadState()
      this.$refs.fileInput.click()
    },
    
    handleOverlayClick(e) {
      if (e.target === e.currentTarget) {
        this.close()
      }
    },
    
    async handleFileSelect(e) {
      if (e.target.files.length > 0) {
        await this.handleFile(e.target.files[0])
      }
    },
    
    async handleDrop(e) {
      this.isDragOver = false
      const files = e.dataTransfer.files
      if (files.length > 0) {
        await this.handleFile(files[0])
      }
    },
    
    async handleFile(file) {
      try {
        // Validate file
        this.validateFile(file, 'image')
        
        this.selectedFile = file
        this.clearPreview()
        this.resetUploadState()
        
        // Create preview immediately for user feedback
        this.previewUrl = URL.createObjectURL(file)
        
        // Auto-upload if upload system is available
        if (this.uploadManager || this.uploadHandler) {
          await this.autoUpload(file)
        }
      } catch (error) {
        alert(error.message)
      }
    },
    
    async insertImage() {
      if (this.activeTab === 'url' && this.imageUrl.trim()) {
        this.$emit('insert', {
          src: this.imageUrl.trim(),
          width: 400,
          height: 300
        })
        this.close()
      } else if (this.activeTab === 'upload' && this.selectedFile) {
        // If upload was successful, use the uploaded URL
        if (this.uploadedUrl) {
          this.insertImageWithSrc(this.uploadedUrl)
        } else if (!this.uploadManager && !this.uploadHandler) {
          // No upload system available, just insert the local file
          const src = URL.createObjectURL(this.selectedFile)
          this.insertImageWithSrc(src)
        } else {
          // Upload system exists but no uploaded URL yet - use local preview
          this.insertImageWithSrc(this.previewUrl)
        }
      }
    },

    insertImageWithSrc(src) {
      this.$emit('insert', {
        src,
        width: 400,
        height: 300
      })
      this.close()
    },

    async insertLocalImage() {
      if (this.selectedFile) {
        const src = URL.createObjectURL(this.selectedFile)
        this.insertImageWithSrc(src)
      }
    }
  },
  
  beforeUnmount() {
    this.clearPreview()
  }
}
</script>

<style scoped>
.bbl-upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.bbl-upload-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.bbl-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.bbl-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
}

.tab-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.tab-btn {
  flex: 1;
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.drop-zone.drag-over {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.05);
}

.drop-zone-icon {
  color: #6b7280;
  margin-bottom: 8px;
  font-size: 24px;
}

.drop-zone-title {
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.drop-zone-subtitle {
  font-size: 14px;
  color: #6b7280;
}

.url-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.url-help {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.bbl-modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
}

.insert-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #3b82f6;
  color: white;
  cursor: pointer;
}

.preview-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-image {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}

.preview-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
}

.preview-filename {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.preview-size {
  font-size: 12px;
  color: #6b7280;
}

.change-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.2s ease;
}

.change-btn:hover {
  background: #f9fafb;
}

.upload-progress {
  margin: 12px 0;
}

.upload-success {
  margin: 12px 0;
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #166534;
}

.success-icon {
  font-size: 16px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  color: #374151;
  text-align: center;
}

.upload-error {
  margin: 12px 0;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #dc2626;
}

.error-icon {
  font-size: 16px;
}

.error-actions {
  display: flex;
  gap: 8px;
}

.retry-btn, .local-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
}

.retry-btn {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.retry-btn:hover {
  background: #2563eb;
}

.local-btn:hover {
  background: #f9fafb;
}

.insert-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>